import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosConfig';
import Modal from 'react-modal';
import './ManagementPages.css';

Modal.setAppElement('#root');

function ZoneManagementPage() {
  const [zones, setZones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // États pour le modal de détails
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneDetails, setZoneDetails] = useState(null);

  // États pour le modal de création
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [newZone, setNewZone] = useState({ nom: '', base: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchZones = useCallback(async () => {
    try {
      setIsLoading(true);

      // Récupérer les zones de la nouvelle table
      const tableResponse = await axiosInstance.get('/admin/zones/table').catch(() => null);
      const zonesFromTable = tableResponse?.data || [];

      // Récupérer les zones existantes (clients + chefs)
      const existingResponse = await axiosInstance.get('/admin/zones/').catch(() => null);
      const zonesFromExisting = existingResponse?.data || [];

      // Fusionner les deux sources
      const zonesMap = new Map();

      // Ajouter les zones de la table (priorité)
      zonesFromTable.forEach(z => {
        zonesMap.set(z.nom, z);
      });

      // Ajouter les zones existantes seulement si pas déjà dans la table
      zonesFromExisting.forEach(z => {
        if (!zonesMap.has(z.zone)) {
          zonesMap.set(z.zone, {
            id: z.zone,
            nom: z.zone,
            base: null,
            description: null,
            est_active: true,
            created_at: new Date().toISOString()
          });
        }
      });

      // Convertir en tableau et trier
      const allZones = Array.from(zonesMap.values()).sort((a, b) => a.nom.localeCompare(b.nom));
      setZones(allZones);

    } catch (error) {
      console.error("Erreur de chargement des zones:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const openCreateModal = () => {
    setNewZone({ nom: '', base: 'DOUALA', description: '' });
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewZone({ nom: '', base: '', description: '' });
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();
    if (!newZone.nom.trim()) {
      alert('Le nom de la zone est obligatoire');
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.post('/admin/zones/table', newZone);
      alert('Zone créée avec succès!');
      closeCreateModal();
      fetchZones();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Erreur lors de la création de la zone');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (zone) => {
    setEditingZone(zone);
    setNewZone({
      nom: zone.nom,
      base: zone.base || 'DOUALA',
      description: zone.description || ''
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingZone(null);
    setNewZone({ nom: '', base: '', description: '' });
  };

  const handleUpdateZone = async (e) => {
    e.preventDefault();
    if (!newZone.nom.trim()) {
      alert('Le nom de la zone est obligatoire');
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.put(`/admin/zones/table/${editingZone.id}`, newZone);
      alert('Zone mise à jour avec succès!');
      closeEditModal();
      fetchZones();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Erreur lors de la mise à jour de la zone');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteZone = async (zoneId, zoneName) => {
    if (!window.confirm(`Supprimer la zone "${zoneName}" ?`)) {
      return;
    }

    try {
      // Si zoneId est un string (nom), l'encoder pour l'URL
      const urlId = typeof zoneId === 'number' ? zoneId : encodeURIComponent(zoneId);
      await axiosInstance.delete(`/admin/zones/table/${urlId}`);
      alert('Zone supprimée avec succès!');
      fetchZones();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Erreur lors de la suppression de la zone');
      }
    }
  };

  // Fonction pour créer une zone legacy dans la table zones
  const handleActivateZone = async (zone) => {
    if (!window.confirm(`Créer la zone "${zone.nom}" dans la table pour pouvoir la modifier ?`)) {
      return;
    }

    try {
      await axiosInstance.post('/admin/zones/table', {
        nom: zone.nom.trim(),
        base: 'DOUALA',
        description: 'Zone importée automatiquement',
        est_active: true
      });
      alert(`Zone "${zone.nom}" activée avec succès! Vous pouvez maintenant la modifier.`);
      fetchZones();
    } catch (error) {
      console.error('Erreur lors de l\'activation:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Erreur lors de l\'activation de la zone');
      }
    }
  };

  const viewZoneDetails = async (zoneName) => {
    try {
      const response = await axiosInstance.get(`/admin/zones/${encodeURIComponent(zoneName)}/details`);
      setZoneDetails(response.data);
      setSelectedZone(zoneName);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error("Erreur lors du chargement des détails:", error);
      alert("Erreur lors du chargement des détails de la zone.");
    }
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setZoneDetails(null);
    setSelectedZone(null);
  };

  if (isLoading) return <div className="loading-container">Chargement...</div>;

  return (
    <div className="management-page">
      <div className="zone-page-header">
        <div>
          <h1 className="zone-page-title">📍 Gestion des Zones</h1>
          <p className="zone-page-subtitle">Créez et gérez les zones géographiques</p>
        </div>
        <button onClick={openCreateModal} className="zone-create-button">
          <span className="zone-create-icon">+</span>
          <span>Nouvelle Zone</span>
        </button>
      </div>

      <div className="zone-info-section">
        <div className="zone-info-card">
          <h3 className="zone-info-title">ℹ️ Comment ça marche ?</h3>
          <div className="zone-info-content">
            <div className="zone-info-item">
              <span className="zone-info-number">1</span>
              <span>Créez une zone manuellement avec le bouton "Nouvelle Zone"</span>
            </div>
            <div className="zone-info-item">
              <span className="zone-info-number">2</span>
              <span>Les zones sont aussi créées automatiquement via les clients et chefs de zone</span>
            </div>
          </div>
          <p className="zone-info-tip">💡 Format recommandé : Douala 1, Douala 2, Yaoundé 1...</p>
        </div>
      </div>

      <div className="zone-table-container">
        <div className="zone-table-header">
          <h3 className="zone-table-title">Liste des Zones ({zones.length})</h3>
        </div>
        {zones.length === 0 ? (
          <div className="zone-empty-state">
            <div className="zone-empty-icon">🗺️</div>
            <p>Aucune zone créée pour le moment</p>
            <button onClick={openCreateModal} className="zone-empty-button">
              Créer la première zone
            </button>
          </div>
        ) : (
          <table className="zone-table">
            <thead>
              <tr>
                <th>Nom de la Zone</th>
                <th>Base</th>
                <th>Description</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {zones.map(zone => (
                <tr key={zone.id} className="zone-table-row">
                  <td>
                    <span className="zone-name-cell">{zone.nom}</span>
                  </td>
                  <td>
                    {zone.base ? (
                      <span className="zone-base-badge">{zone.base}</span>
                    ) : (
                      <span className="zone-base-empty">-</span>
                    )}
                  </td>
                  <td>
                    <span className="zone-description-cell">{zone.description || '-'}</span>
                  </td>
                  <td>
                    <span className={`zone-status-badge ${zone.est_active ? 'active' : 'inactive'}`}>
                      {zone.est_active ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </td>
                  <td className="zone-actions-cell">
                    <button
                      className="zone-action-btn zone-action-view"
                      onClick={() => viewZoneDetails(zone.nom)}
                      title="Voir Détails"
                    >
                      👁️
                    </button>
                    {/* Pour les zones avec un vrai ID numérique : Afficher Modifier/Supprimer */}
                    {typeof zone.id === 'number' ? (
                      <>
                        <button
                          className="zone-action-btn zone-action-edit"
                          onClick={() => openEditModal(zone)}
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          className="zone-action-btn zone-action-delete"
                          onClick={() => handleDeleteZone(zone.id, zone.nom)}
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </>
                    ) : (
                      /* Pour les zones legacy : Afficher un bouton "Activer" */
                      <button
                        className="zone-action-btn zone-action-activate"
                        onClick={() => handleActivateZone(zone)}
                        title="Créer dans la table pour pouvoir modifier"
                        style={{ backgroundColor: '#17a2b8', color: 'white' }}
                      >
                        ✅ Activer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de création */}
      <Modal
        isOpen={isCreateModalOpen}
        onRequestClose={closeCreateModal}
        className="zone-modal"
        overlayClassName="zone-modal-overlay"
      >
        <div className="zone-modal-content">
          <div className="zone-modal-header">
            <div>
              <h2 className="zone-modal-title">➕ Créer une nouvelle Zone</h2>
              <p className="zone-modal-subtitle">Remplissez les informations pour créer une zone</p>
            </div>
            <button onClick={closeCreateModal} className="zone-modal-close">✕</button>
          </div>
          <form onSubmit={handleCreateZone} className="zone-modal-form">
            <div className="zone-form-group">
              <label className="zone-form-label">
                Nom de la zone <span className="zone-form-required">*</span>
              </label>
              <input
                type="text"
                className="zone-form-input"
                value={newZone.nom}
                onChange={(e) => setNewZone({...newZone, nom: e.target.value})}
                placeholder="Ex: Douala 5, Douala 3..."
                required
                autoFocus
              />
            </div>
            <div className="zone-form-group">
              <label className="zone-form-label">Base</label>
              <select
                className="zone-form-select"
                value={newZone.base}
                onChange={(e) => setNewZone({...newZone, base: e.target.value})}
              >
                <option value="DOUALA">DOUALA</option>
                <option value="YAOUNDE">YAOUNDE</option>
                <option value="BAFOUSSAM">BAFOUSSAM</option>
                <option value="GAROUA">GAROUA</option>
                <option value="BOUA">BOUA</option>
                <option value="BERTOUA">BERTOUA</option>
                <option value="DOUALA">DOUALA</option>
                <option value="NGAOUNDéré">NGAOUNDéré</option>
                <option value="BAMENDA">BAMENDA</option>
                <option value="MAROUA">MAROUA</option>
              </select>
            </div>
            <div className="zone-form-group">
              <label className="zone-form-label">Description (optionnel)</label>
              <textarea
                className="zone-form-textarea"
                value={newZone.description}
                onChange={(e) => setNewZone({...newZone, description: e.target.value})}
                placeholder="Description de la zone..."
                rows="3"
              />
            </div>
            <div className="zone-modal-actions">
              <button type="button" onClick={closeCreateModal} className="zone-modal-btn zone-modal-btn-cancel">
                Annuler
              </button>
              <button type="submit" className="zone-modal-btn zone-modal-btn-confirm" disabled={isSubmitting}>
                {isSubmitting ? '🔄 Création...' : '✓ Créer la zone'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Modal de modification */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={closeEditModal}
        className="zone-modal"
        overlayClassName="zone-modal-overlay"
      >
        <div className="zone-modal-content">
          <div className="zone-modal-header">
            <div>
              <h2 className="zone-modal-title">✏️ Modifier la Zone</h2>
              <p className="zone-modal-subtitle">Modifiez les informations de la zone</p>
            </div>
            <button onClick={closeEditModal} className="zone-modal-close">✕</button>
          </div>
          <form onSubmit={handleUpdateZone} className="zone-modal-form">
            <div className="zone-form-group">
              <label className="zone-form-label">
                Nom de la zone <span className="zone-form-required">*</span>
              </label>
              <input
                type="text"
                className="zone-form-input"
                value={newZone.nom}
                onChange={(e) => setNewZone({...newZone, nom: e.target.value})}
                required
                autoFocus
              />
            </div>
            <div className="zone-form-group">
              <label className="zone-form-label">Base</label>
              <select
                className="zone-form-select"
                value={newZone.base}
                onChange={(e) => setNewZone({...newZone, base: e.target.value})}
              >
                <option value="">Aucune base</option>
                <option value="DOUALA">DOUALA</option>
                <option value="YAOUNDE">YAOUNDE</option>
                <option value="BAFOUSSAM">BAFOUSSAM</option>
                <option value="GAROUA">GAROUA</option>
                <option value="BOUA">BOUA</option>
                <option value="BERTOUA">BERTOUA</option>
                <option value="DOUALA">DOUALA</option>
                <option value="NGAOUNDéré">NGAOUNDéré</option>
                <option value="BAMENDA">BAMENDA</option>
                <option value="MAROUA">MAROUA</option>
              </select>
            </div>
            <div className="zone-form-group">
              <label className="zone-form-label">Description</label>
              <textarea
                className="zone-form-textarea"
                value={newZone.description}
                onChange={(e) => setNewZone({...newZone, description: e.target.value})}
                rows="3"
              />
            </div>
            <div className="zone-form-group zone-form-checkbox">
              <label className="zone-checkbox-label">
                <input
                  type="checkbox"
                  className="zone-checkbox"
                  checked={editingZone?.est_active}
                  onChange={(e) => setEditingZone({...editingZone, est_active: e.target.checked})}
                />
                <span>Zone active</span>
              </label>
            </div>
            <div className="zone-modal-actions">
              <button type="button" onClick={closeEditModal} className="zone-modal-btn zone-modal-btn-cancel">
                Annuler
              </button>
              <button type="submit" className="zone-modal-btn zone-modal-btn-confirm" disabled={isSubmitting}>
                {isSubmitting ? '🔄 Mise à jour...' : '💾 Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailsModalOpen}
        onRequestClose={closeDetailsModal}
        className="zone-modal zone-modal-large"
        overlayClassName="zone-modal-overlay"
      >
        {zoneDetails && (
          <div className="zone-modal-content">
            <div className="zone-modal-header">
              <div>
                <h2 className="zone-modal-title">📍 Zone : {selectedZone}</h2>
                <p className="zone-modal-subtitle">Détails de la zone</p>
              </div>
              <button onClick={closeDetailsModal} className="zone-modal-close">✕</button>
            </div>

            <div className="zone-details-content">
              <div className="zone-detail-section">
                <h4 className="zone-detail-title">
                  <span className="zone-detail-icon">👔</span>
                  Chefs de Zone ({zoneDetails.chefs_zone.length})
                </h4>
                {zoneDetails.chefs_zone.length === 0 ? (
                  <p className="zone-detail-empty">Aucun chef de zone</p>
                ) : (
                  <ul className="zone-detail-list">
                    {zoneDetails.chefs_zone.map(chef => (
                      <li key={chef.id} className="zone-detail-item">{chef.nom} <span className="zone-detail-id">(ID: {chef.id})</span></li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="zone-detail-section">
                <h4 className="zone-detail-title">
                  <span className="zone-detail-icon">💼</span>
                  Commerciaux ({zoneDetails.commerciaux.length})
                </h4>
                {zoneDetails.commerciaux.length === 0 ? (
                  <p className="zone-detail-empty">Aucun commercial</p>
                ) : (
                  <ul className="zone-detail-list">
                    {zoneDetails.commerciaux.map(com => (
                      <li key={com.id} className="zone-detail-item">{com.nom} <span className="zone-detail-id">(ID: {com.id})</span></li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="zone-detail-section">
                <h4 className="zone-detail-title">
                  <span className="zone-detail-icon">🏪</span>
                  Clients ({zoneDetails.clients.length})
                </h4>
                {zoneDetails.clients.length === 0 ? (
                  <p className="zone-detail-empty">Aucun client</p>
                ) : (
                  <ul className="zone-detail-list zone-detail-list-scrollable">
                    {zoneDetails.clients.map(client => (
                      <li key={client.id} className="zone-detail-item">{client.nom} <span className="zone-detail-id">(ID: {client.id})</span></li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="zone-modal-actions">
              <button type="button" onClick={closeDetailsModal} className="zone-modal-btn zone-modal-btn-confirm">
                Fermer
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ZoneManagementPage;
